import React, { useEffect, useState, useRef } from "react";
import Modal from "@mui/material/Modal";
import { useSharedValue } from "./Constants/constants";

const loadScript = (src, id) => {
  return new Promise((resolve, reject) => {
    // Check if the script is already loaded
    if (document.getElementById(id)) {
      resolve();
      return;
    }

    const script = document.createElement("script");
    script.src = src;
    script.id = id;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load the script ${src}`));
    document.head.appendChild(script);
  });
};

function Chat() {
  const [directLine, setDirectLine] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageEntities, setMessageEntities] = useState([]);
  const messagesContainerRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const { isChatWindowVisible, updateDisplayChatWindowValue } = useSharedValue();
  const [inputValue, setInputValue] = useState("");
  const [textLength, setTextLength] = useState(0);
  const [botMessages, setBotMessages] = useState([]);
  const pulseRingRef = useRef(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pdfName, setPdfName] = useState("");
  const [modalContent, setModalContent] = useState("");
  const hasDisplayedWelcome = useRef(false);

  useEffect(() => {
    // Prevent duplicate welcome message
    if (hasDisplayedWelcome.current) return;
    hasDisplayedWelcome.current = true;

    const userDataJSON = localStorage.getItem("token");
    let userName = "there";

    if (userDataJSON) {
      const userData = JSON.parse(userDataJSON);

      userName = userData.firstName + " " + userData.lastName;

      // Make sure 'email' property exists in your data.
    }
    displayMessage(`Hi ${userName} 👋 How can I help you today?`, "bot");
  }, []);

  useEffect(() => {
    // Scroll to bottom whenever messages update
    const messagesContainer = messagesContainerRef.current;
    if (messagesContainer) {
      messagesContainer.scrollTop = messagesContainer.scrollHeight;
    }
  }, [messages]);

  const displayMessage = (text, sender) => {
    const updatedText = text.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (match, p1, p2) => {
      return `<a href="#" onclick="openModal('${p2}')">Citation-${p1}</a>`;
    });

    if (sender === "bot") {
      // Clean the text by removing citation reference lines before storing
      const cleanedText = text.replace(/\n\[\d+\]:\s*cite:\d+\s*"[^"]*"/g, "");
      setBotMessages((prevBotMessages) => [...prevBotMessages, cleanedText]);
      // Replace the loader message with the actual bot message
      setMessages((prevMessages) => {
        const index = prevMessages.findIndex((msg) => msg.isLoader);
        if (index !== -1) {
          // Remove the loader and add the actual message with cleaned text
          return [...prevMessages.slice(0, index), { text: cleanedText, sender }];
        }
        // In case there's no loader, just add the message with cleaned text
        return [...prevMessages, { text: cleanedText, sender }];
      });
    } else {
      // For user messages, just add them to the list
      setMessages((prevMessages) => [...prevMessages, { text: updatedText, sender }]);
    }
  };

  // Initialize chat
  useEffect(() => {
    loadScript(
      "https://cdn.botframework.com/botframework-webchat/latest/webchat.js",
      "webchat-script"
    )
      .then(() => {
        initializeChat();
      })
      .catch((error) => console.error("Failed to load WebChat script:", error));
  }, []);

  const handleClearHistory = () => {
    setMessages([]);
  };
  const initializeChat = () => {
    // Check if WebChat is available
    if (!window.WebChat) {
      console.error("WebChat SDK not loaded");
      return;
    }

    // Add timeout to the fetch request
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

    fetch(
      "https://default33e1a6d41ba1495e871e1dc2f0c47b.46.environment.api.powerplatform.com/powervirtualagents/botsbyschema/cr064_whsCopilot9wKE_D/directline/token?api-version=2022-03-01-preview",
      {
        signal: controller.signal,
      }
    )
      .then((response) => {
        clearTimeout(timeoutId);
        if (!response.ok) {
          throw new Error(`Network response was not ok, status: ${response.status}`);
        }
        return response.json();
      })
      .then((data) => {
        if (!data.token) {
          throw new Error("No token received from server");
        }

        const newDirectLine = window.WebChat.createDirectLine({ token: data.token });
        setDirectLine(newDirectLine);

        newDirectLine.activity$.subscribe(
          (activity) => {
            if (activity.type === "message" && activity.from.role === "bot") {
              displayMessage(activity.text, "bot");

              setMessageEntities((prevMessages) => {
                const updated = [...prevMessages, activity.entities || []];

                return updated;
              });
            }
          },
          (error) => console.error("Failed to subscribe to activities:", error)
        );
      })
      .catch((error) => {
        clearTimeout(timeoutId);
        console.error("Error initializing chat:", error);

        // Show user-friendly error message
        displayMessage(
          "Unable to connect to Copilot service. Please check your internet connection and try again later.",
          "bot"
        );

        // If it's a timeout or network error, suggest retry
        if (error.name === "AbortError" || error.message.includes("Failed to fetch")) {
          setTimeout(() => {
            displayMessage("Click here to retry connection.", "bot");
          }, 1000);
        }
      });
  };

  const sendMessage = (text) => {
    const token = localStorage.getItem("CopilotToken");
    if (!text.trim()) return;

    displayMessage(text, "user");

    // Add a loader message
    const loaderMessage = { text: "Loading...", sender: "bot", isLoader: true };
    setMessages((prevMessages) => [...prevMessages, loaderMessage]);

    if (directLine) {
      const messageWithToken = `${text}-${token}`;
      directLine
        .postActivity({
          from: { id: "user", name: "user" },
          type: "message",
          text: messageWithToken,
        })
        .subscribe(
          (id) => {
            setInputValue("");
            // Don't set isLoading here since we are using a loader message
          },
          (error) => console.error(`Error sending message: ${error}`)
        );
    } else {
      console.error("DirectLine is not initialized.");
    }
  };

  // Chat window and message styles
  const chatWindowStyle = {
    position: "fixed",
    bottom: "0",
    right: "0",
    width: "400px",
    height: `calc(100vh - 66px)`,
    backgroundColor: "#F2F3F4",
    display: "flex",
    flexDirection: "column",
    borderRadius: "6px",
  };

  const messagesStyle = {
    flex: "1",
    padding: "10px",
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    scrollbarWidth: "none" /* Firefox */,
    msOverflowStyle: "none" /* IE 10+ */,
    "::-webkit-scrollbar": {
      display: "none" /* Chrome, Safari, Opera */,
    },
  };

  const messageStyle = (sender) => ({
    color: sender === "bot" ? "black" : "white",
    backgroundColor: sender === "bot" ? "white" : "#2f4869",
    padding: "10px",
    borderRadius: "10px",
    alignSelf: sender === "bot" ? "flex-start" : "flex-end",
    maxWidth: "80%",
    wordWrap: "break-word", // Ensures text breaks onto next line
    whiteSpace: "pre-wrap", // Ensures whitespace is considered
    fontSize: "14px", // Smaller text size
  });
  const pulseRingStyle = {
    content: "",
    width: "28px",
    height: "28px",
    border: "5px solid #2aa7e0",
    borderRadius: "50%",
    position: "absolute",
    display: "none",
    bottom: "22.5px",
    transform: "translateY(-50%)",
    right: "41.5px",
    animation: "pulsate infinite 1.5s",
  };

  const inputStyle = {
    padding: "10px",
    border: "none",
    outline: "none",
    width: "calc(100% - 0px)",
    borderRadius: "6px",
    // borderTopLeftRadius: '10px',
    // borderTopRightRadius: '10px',
    // borderBottomLeftRadius: '10px',
    // borderBottomRightRadius: '10px',
  };
  // Event handler for input change
  const handleInputChange = (event) => {
    const { value } = event.target;
    setInputValue(value);
    setTextLength(value.length);
  };
  const handleCitationClick = (citationId, messageIndex) => {
    let entity = null;

    if (messageEntities && messageEntities.length > 0 && messageEntities[0]) {
      const entities = messageEntities[0]; // Use first entities array

      if (Array.isArray(entities)) {
        // The entity structure shows that citation entities are at index 1, not 0
        // Look for entity with citation data
        entity = entities.find(
          (ent) => ent && ent.citation && Array.isArray(ent.citation) && ent.citation.length > 0
        );

        if (entity && entity.citation) {
          // Citations are indexed starting at 1, but array is 0-based
          const citationIndex = parseInt(citationId) - 1;

          // First try to get citation by array index (this works for both cite:X and URL citations)
          if (citationIndex >= 0 && citationIndex < entity.citation.length) {
            entity = entity.citation[citationIndex];
          } else {
            // Fallback: try to find by ID string
            const citationString = `cite:${citationId}`;

            const specificCitation = entity.citation.find((cite) => cite["@id"] === citationString);

            if (specificCitation) {
              entity = specificCitation;
            } else {
              console.warn("⚠ Citation not found by index or ID");
            }
          }
        }

        // Fallback: try to get entity by direct index
        if (!entity && entities[1] && entities[1]["@id"] === `cite:${citationId}`) {
          entity = entities[1];
        }
      }
    } else {
      console.warn("No message entities available");
    }

    if (entity) {
      // Extract content from the entity
      let content = "";
      let title = "";

      if (entity.text) {
        content = String(entity.text);
      } else if (entity.appearance && entity.appearance.text) {
        content = String(entity.appearance.text);
      } else if (entity.appearance && entity.appearance.snippet) {
        content = String(entity.appearance.snippet);
      } else if (entity.appearance && entity.appearance.abstract) {
        content = String(entity.appearance.abstract);
      } else if (entity.content) {
        content = String(entity.content);
      } else {
        // Try to find content in any property
        for (const [_key, value] of Object.entries(entity)) {
          if (typeof value === "string" && value.length > 50) {
            content = value;

            break;
          }
        }

        if (!content) {
          console.warn("✗ No content found in any property");
          content = "Citation content available but format not recognized.";
        }
      }

      if (entity.appearance && entity.appearance.name) {
        title = entity.appearance.name;
      } else if (entity.name) {
        title = entity.name;
      } else if (entity.title) {
        title = entity.title;
      } else if (entity["@id"] && entity["@id"].startsWith("http")) {
        // For URL-based citations, use the URL or extract domain

        try {
          const url = new URL(entity["@id"]);
          title = url.hostname || entity["@id"];
        } catch {
          title = entity["@id"];
        }
      } else {
        title = `Citation ${citationId}`;
      }

      // Remove .pdf extension from the title
      const nameWithoutExtension = title.replace(/\.pdf$/i, "");

      // Ensure we have valid content
      const finalContent =
        content && content.trim() ? content : "No content available for this citation.";
      const finalTitle = nameWithoutExtension || `Citation ${citationId}`;

      // Only open modal if we have valid content
      if (finalContent && finalContent !== "No content available for this citation.") {
        setModalContent(finalContent);
        setPdfName(finalTitle);
        setIsModalOpen(true);
      } else {
        setModalContent("Content not found for the selected citation.");
        setPdfName("Citation Not Found");
        setIsModalOpen(true);
      }
    } else {
      console.error("✗ No entity found for citation", citationId);
      setModalContent("Content not found for the selected citation.");
      setPdfName("Citation Not Found");
      setIsModalOpen(true);
    }
  };
  const parseMessageForComponents = (message) => {
    const urlRegex = /(https?:\/\/[^\s]+)/g;

    // Remove the citation reference lines like "[1]: cite:1 "Citation-1""
    const cleanedMessage = message.replace(/\n\[\d+\]:\s*cite:\d+\s*"[^"]*"/g, "");

    // Split the message by citations and URLs, while keeping them in the result
    const parts = cleanedMessage.split(/(\[\d+\]|https?:\/\/[^\s]+)/g).filter(Boolean);

    return parts.map((part, index) => {
      if (/\[\d+\]/.test(part)) {
        // Extract the citation ID, assuming the format [1]
        const citationId = part.match(/\[(\d+)\]/)[1];
        // Return a clickable element for the citation

        return (
          <span
            key={index}
            style={{
              color: "#2563eb",
              textDecoration: "underline",
              cursor: "pointer",
              backgroundColor: "#eff6ff",
              padding: "2px 4px",
              borderRadius: "3px",
              fontSize: "12px",
              fontWeight: "500",
              marginLeft: "2px",
              marginRight: "2px",
            }}
            onClick={() => {
              const messageIndex = botMessages.indexOf(message);

              handleCitationClick(citationId, messageIndex);
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = "#dbeafe";
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = "#eff6ff";
            }}
            title={`View Citation ${citationId}`}
          >
            {part}
          </span>
        );
      } else if (urlRegex.test(part)) {
        // It's a URL, return a clickable element for the URL
        return (
          <a
            key={index}
            href={part}
            style={{ color: "blue", textDecoration: "underline" }}
            target="_blank"
            rel="noopener noreferrer"
          >
            {part}
          </a>
        );
      } else {
        // It's just regular text
        return part;
      }
    });
  };

  const renderCitationReferences = (messageText, messageIndex) => {
    // Clean the message first to remove citation reference lines
    const cleanedMessage = messageText.replace(/\n\[\d+\]:\s*cite:\d+\s*"[^"]*"/g, "");

    // Extract all citation numbers from the cleaned message
    const citationMatches = cleanedMessage.match(/\[(\d+)\]/g);
    if (!citationMatches || citationMatches.length === 0) return null;

    // Get unique citations
    const uniqueCitations = [...new Set(citationMatches)];

    const botMessageIndex = botMessages.findIndex((msg) => msg === messageText);

    return (
      <div
        style={{
          marginTop: "12px",
          paddingTop: "8px",
          borderTop: "1px solid #e5e7eb",
          fontSize: "11px",
        }}
      >
        <div
          style={{
            color: "#6b7280",
            marginBottom: "6px",
            fontWeight: "500",
          }}
        >
          {uniqueCitations.length} reference{uniqueCitations.length > 1 ? "s" : ""}
        </div>
        {uniqueCitations.map((citation, index) => {
          const citationId = citation.match(/\[(\d+)\]/)[1];
          return (
            <div
              key={index}
              style={{
                display: "flex",
                alignItems: "center",
                marginBottom: "4px",
                padding: "4px 8px",
                backgroundColor: "#f8f9fa",
                borderRadius: "4px",
                cursor: "pointer",
              }}
              onClick={() => handleCitationClick(citationId, botMessageIndex)}
            >
              <span
                style={{
                  minWidth: "16px",
                  height: "16px",
                  backgroundColor: "#2563eb",
                  color: "white",
                  borderRadius: "50%",
                  fontSize: "9px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginRight: "8px",
                  fontWeight: "600",
                }}
              >
                {citationId}
              </span>
              <span style={{ color: "#374151", fontSize: "11px" }}>Extractor Documentation</span>
            </div>
          );
        })}
      </div>
    );
  };

  const parseModalContent = (content) => {
    if (!content || content.trim() === "") {
      return (
        <div
          style={{
            textAlign: "center",
            color: "#666",
            fontStyle: "italic",
            padding: "20px",
          }}
        >
          No content available for this citation.
        </div>
      );
    }

    // Check if content contains XML-style page tags (use global flag to find all matches)
    const pageMatches = [...content.matchAll(/<page value="(\d+)">(.*?)<\/page>/gs)];

    if (pageMatches && pageMatches.length > 0) {
      return (
        <div>
          {pageMatches.map((match, index) => {
            const pageNumber = match[1];
            const pageContent = match[2];

            return (
              <div
                key={`page-${pageNumber}`}
                style={{ marginBottom: index < pageMatches.length - 1 ? "20px" : "0" }}
              >
                <div
                  style={{
                    backgroundColor: "#e3f2fd",
                    padding: "8px 12px",
                    borderRadius: "4px",
                    marginBottom: "12px",
                    fontSize: "12px",
                    color: "#1565c0",
                    fontWeight: "500",
                  }}
                >
                  📄 Page {pageNumber}
                </div>
                <div
                  style={{
                    whiteSpace: "pre-wrap",
                    lineHeight: "1.5",
                    paddingLeft: "8px",
                    borderLeft: "3px solid #e3f2fd",
                  }}
                >
                  {pageContent.trim()}
                </div>
              </div>
            );
          })}
        </div>
      );
    }

    // Handle regular text content

    return <div style={{ whiteSpace: "pre-wrap" }}>{content}</div>;
  };

  const openModal = (citationId) => {
    // Here you would fetch the content or find it in your entities
    // For now, we'll just display the citation ID
    setModalContent(`Content for ${citationId}`);
    setIsModalOpen(true);
  };

  // Make sure to bind this function to window so it can be called from onclick within dangerouslySetInnerHTML
  window.openModal = openModal;

  const handleVoiceRecognition = () => {
    const recognition = new (window.SpeechRecognition || window.webkitSpeechRecognition)();
    recognition.lang = "en-US";
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;

    recognition.onstart = () => {
      // Show the pulse ring animation when recording starts
      pulseRingRef.current.style.display = "block";
    };

    recognition.onresult = (event) => {
      const speechResult = event.results[0][0].transcript;
      setInputValue(speechResult);
      sendMessage(speechResult);
    };

    recognition.onerror = (event) => {
      console.error("Speech recognition error", event.error);
    };

    recognition.onend = () => {
      // Hide the pulse ring animation when recording stops
      pulseRingRef.current.style.display = "none";
    };

    recognition.start();
  };
  const toggleChatWindow = () => {
    updateDisplayChatWindowValue(false);
  };
  return (
    <>
      <style>
        {`
                    @keyframes blink {
                        0% { opacity: .2; }
                        20% { opacity: 1; }
                        100% { opacity: .2; }
                    }
                    .loader-dot {
                        animation: blink 1.4s infinite both;
                        display: inline-block;
                        width: 8px;
                        height: 8px;
                        border-radius: 50%;
                        margin: 0 2px;
                    }
                    .loader-dot:nth-child(1) { background-color: black; animation-delay: -0.32s; }
                    .loader-dot:nth-child(2) { background-color: black; animation-delay: -0.16s; }
                    .loader-dot:nth-child(3) { background-color: black; }
                `}
      </style>
      <div style={chatWindowStyle}>
        <div
          style={{
            height: "40px",
            width: "100%",
            backgroundColor: "#F2F3F4",
            color: "black",
            display: "flex",
            alignItems: "center",
            borderBottom: "1px solid #e0e0e0",
          }}
        >
          <p
            style={{
              fontSize: "18px",
              fontWeight: "500",
              marginLeft: "10px",
              marginTop: "auto",
              marginBottom: "auto",
            }}
          >
            Powered by Copilot
          </p>
          <button
            onClick={toggleChatWindow}
            style={{
              background: "none",
              border: "none",
              outline: "none",
              cursor: "pointer",
              marginLeft: "auto",
              paddingRight: "10px",
            }}
          >
            <svg width="12" height="12" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M1 1L11 11M1 11L11 1" stroke="black" strokeWidth="1" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        <div ref={messagesContainerRef} style={messagesStyle}>
          {messages.map((msg, index) =>
            msg.isLoader ? (
              // Render a loader component or element for the loader message
              <div
                key={index}
                style={{ alignSelf: "flex-start", display: "flex", alignItems: "center" }}
              >
                <span className="loader-dot red"></span>
                <span className="loader-dot green"></span>
                <span className="loader-dot blue"></span>
              </div>
            ) : (
              // Render regular messages
              <div key={index} style={messageStyle(msg.sender)}>
                {parseMessageForComponents(msg.text)}
                {msg.sender === "bot" && renderCitationReferences(msg.text, index)}
              </div>
            )
          )}
        </div>
        <div style={{ height: 80, background: "white", borderRadius: 6, margin: 10 }}>
          <input
            style={inputStyle}
            type="text"
            value={inputValue} // Controlled input
            onChange={handleInputChange}
            onKeyDown={(e) => e.key === "Enter" && sendMessage(e.target.value)}
            placeholder="Type your message"
          />
          <div style={{ textAlign: "left", padding: "0 10px" }}>
            {textLength}/2000
            <button
              onClick={() => {
                sendMessage(inputValue);
              }}
              style={{
                alignSelf: "flex-end",
                border: "none",
                background: "transparent",
                position: "absolute",
                right: 14,
              }}
            >
              <img src="/sendicon.png" alt="Send" style={{ height: "15px", width: "17px" }}></img>
            </button>
            <button
              onClick={() => {
                handleVoiceRecognition();
              }}
              style={{
                alignSelf: "flex-end",
                border: "none",
                background: "transparent",
                position: "absolute",
                right: 40,
              }}
            >
              <img src="/mic.png" alt="Microphone" style={{ height: "18px", width: "18px" }}></img>
            </button>
            <button
              onClick={() => {
                handleClearHistory();
              }}
              style={{
                alignSelf: "flex-end",
                border: "none",
                background: "transparent",
                position: "absolute",
                right: 65,
              }}
            >
              {/* <p style ="color:black;font-size:18px"> 𝗖𝗹𝗲𝗮𝗿 </p> */}
              <img src="/remove.png" alt="Clear" style={{ height: "20px", width: "20px" }}></img>
            </button>
            <div ref={pulseRingRef} style={pulseRingStyle}></div>
          </div>
        </div>

        <Modal
          open={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          aria-labelledby="delete-confirmation-modal-title"
          aria-describedby="delete-confirmation-modal-description"
          style={{
            border: "none", // Remove border
            background: "transparent", // Remove background color
            boxShadow: "none",
          }}
        >
          <div
            style={{
              top: "50%",
              left: "50%",
              border: "none",
              transform: "translate(-50%, -50%)",
              position: "absolute",
              width: "700px",
              overflow: "auto",

              height: "600px",
              padding: "10px",

              backgroundColor: "white",
              boxShadow: "none",
              borderRadius: "8px",
            }}
          >
            {/* Close icon */}
            <div style={{ textAlign: "right" }}>
              <button
                style={{ background: "none", border: "none", cursor: "pointer" }}
                onClick={() => setIsModalOpen(false)}
              >
                &#10006; {/* Unicode character for close icon */}
              </button>
            </div>
            {/* Title */}
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                paddingBottom: "10px",
                borderBottom: "1px solid #e9ecef",
                marginBottom: "15px",
              }}
            >
              <h3
                id="delete-confirmation-modal-title"
                style={{
                  margin: "0",
                  color: "#343a40",
                  fontWeight: "600",
                  fontSize: "18px",
                }}
              >
                {pdfName}
              </h3>
            </div>
            {/* Content */}
            <div
              style={{
                marginTop: "20px",
                fontSize: "14px",
                lineHeight: "1.6",
                maxHeight: "500px",
                overflowY: "auto",
                padding: "10px",
                backgroundColor: "#f8f9fa",
                borderRadius: "6px",
                border: "1px solid #e9ecef",
              }}
            >
              {parseModalContent(modalContent)}
            </div>
          </div>
        </Modal>
      </div>
    </>
  );
}

export default Chat;
